import { useCallback, useContext, useSyncExternalStore } from 'react';
import { StoreState } from 'src/types/global-types';
import { DeviceClassContext } from 'src/core/engine/deviceClass';
import { shallowEqual, useSelector } from 'react-redux';
import Bugsnag from '@bugsnag/js';
import { toNotifiableError } from 'src/util/general';

export enum Breakpoint {
    Mob = 'Mob',
    Desk = 'Desk',
}

const MQ = '(max-width: 899px)';

export function useBreakpoint(mediaQuery: string = MQ, _name = 'useBreakpoint'): [Breakpoint, any] {
    // The server snapshot is also used for the hydration render, so it follows the device
    // class SSR rendered for. A wrong guess is corrected by a re-render straight after hydration.
    const device = useContext(DeviceClassContext);
    const getServerSnapshot = useCallback(() => (device === 'desktop' ? Breakpoint.Desk : Breakpoint.Mob), [device]);

    const subscribe = useCallback(
        (onChange: () => void) => {
            const mql = window.matchMedia(mediaQuery);
            try {
                // Chrome & Firefox
                mql.addEventListener('change', onChange);
            } catch (e1) {
                try {
                    // Safari
                    mql.addListener(onChange);
                } catch (e2) {
                    Bugsnag.notify(toNotifiableError(e2));
                }
            }
            return () => {
                try {
                    // Chrome & Firefox
                    mql.removeEventListener('change', onChange);
                } catch (e1) {
                    try {
                        // Safari
                        mql.removeListener(onChange);
                    } catch (e2) {
                        Bugsnag.notify(toNotifiableError(e2));
                    }
                }
            };
        },
        [mediaQuery],
    );

    const getSnapshot = useCallback(
        () => (window.matchMedia(mediaQuery).matches ? Breakpoint.Mob : Breakpoint.Desk),
        [mediaQuery],
    );

    const breakpoint = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    // No call site uses the setter; the tuple shape is kept for compatibility.
    return [breakpoint, noop];
}

const noop = () => undefined;

export function useBreakpointMatch() {
    const [bp] = useBreakpoint();
    return {
        isMob: bp === Breakpoint.Mob,
        isDesk: bp === Breakpoint.Desk,
    };
}

const selector = (s: StoreState) => s.app.renderEnv;
export function useRenderEnv() {
    const renderEnv = useSelector(selector, shallowEqual);
    return {
        isBrowser: renderEnv === 'browser',
        isServer: renderEnv === 'server',
    };
}
